// Las piezas visuales que comparten los paneles de la ficha MDM: la card, la métrica y la franja.
// Viven aquí y no en la pestaña porque el panel de resultado de la consulta pinta las mismas.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { HelpTooltip } from '@/kit';

export const CARD_TITLE = 'text-[11.5px] font-bold uppercase tracking-[0.04em]';
export const METRIC_LABEL =
    'text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';
export const SUB_LINE = 'mt-1 text-[11px] leading-relaxed';

export function PanelCard({
    title,
    meta,
    danger,
    help,
    children,
}: {
    title: string;
    meta?: ReactNode;
    danger?: boolean;
    /** El porqué de la card, en el `?` del título: en pantalla queda el dato, no la prosa. */
    help?: string;
    children: ReactNode;
}) {
    return (
        <section
            className={cn(
                'overflow-hidden rounded-lg border bg-card',
                danger ? 'border-destructive/40' : 'border-border',
            )}
        >
            <header
                className={cn(
                    'flex items-center justify-between gap-2 border-b px-3 py-2',
                    danger
                        ? 'border-destructive/30 bg-destructive/10'
                        : 'border-border bg-muted/40',
                )}
            >
                <h3
                    className={cn(
                        CARD_TITLE,
                        'flex items-center gap-1',
                        danger ? 'text-destructive' : 'text-foreground',
                    )}
                >
                    {title}
                    {help ? <HelpTooltip content={help} label={`Ayuda: ${title}`} /> : null}
                </h3>
                {meta ? <span className="text-[10px] text-muted-foreground">{meta}</span> : null}
            </header>
            <div className="space-y-3 p-3">{children}</div>
        </section>
    );
}

export function Metric({
    label,
    value,
    tone = 'neutral',
    mono,
    wrap,
    help,
}: {
    label: string;
    value: ReactNode;
    tone?: 'ok' | 'warn' | 'danger' | 'neutral';
    mono?: boolean;
    /** El valor baja de renglón en vez de recortarse; para los códigos que nunca caben en una línea. */
    wrap?: boolean;
    /** De dónde sale el valor, o por qué está vacío. Va en el `?` de la etiqueta. */
    help?: string;
}) {
    return (
        <div className="min-w-0">
            <div className={cn(METRIC_LABEL, 'flex items-center gap-1')}>
                {label}
                {help ? <HelpTooltip content={help} label={`Ayuda: ${label}`} /> : null}
            </div>
            <div
                className={cn(
                    'mt-0.5 text-[11.5px] font-semibold tabular-nums',
                    wrap ? 'break-words leading-tight' : 'truncate',
                    mono && 'font-mono',
                    tone === 'ok' && 'text-success',
                    tone === 'warn' && 'text-warning',
                    tone === 'danger' && 'text-destructive',
                )}
            >
                {value}
            </div>
        </div>
    );
}

export function InfoStrip({
    tone,
    children,
}: {
    tone: 'neutral' | 'warn' | 'danger' | 'success';
    children: ReactNode;
}) {
    return (
        <div
            className={cn(
                'rounded-md border px-3 py-2 text-[11.5px] leading-relaxed',
                tone === 'neutral' && 'border-border bg-muted/40 text-muted-foreground',
                tone === 'warn' && 'border-warning-soft-border bg-warning-soft text-warning',
                tone === 'danger' && 'border-destructive/40 bg-destructive/10 text-destructive',
                tone === 'success' && 'border-success-soft-border bg-success-soft text-success',
            )}
        >
            {children}
        </div>
    );
}
